import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
export default function FamilyOfficeDemo() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const enterpriseFeatures = [
    {
      icon: "🏛️",
      title: "Infrastructure dédiée",
      description: "Instance privée, hébergement européen sur infrastructure certifiée, backup quotidien chiffré.",
    },
    {
      icon: "⚖️",
      title: "Conformité multi-juridictions",
      description: "Reporting fiscal France, Suisse, UK, Luxembourg. Exports adaptés aux obligations déclaratives.",
    },
    {
      icon: "🔐",
      title: "Sécurité renforcée",
      description: "SSO, 2FA obligatoire, audit logs complets, conformité RGPD, ISO 27001.",
    },
    {
      icon: "🔄",
      title: "Intégration ERP/PMS",
      description: "Connecteurs pour SAP, Oracle, Salesforce, QuickBooks. API GraphQL et REST.",
    },
    {
      icon: "👨‍💼",
      title: "Account Manager dédié",
      description: "Point de contact unique, réunions trimestrielles stratégiques, roadmap personnalisée.",
    },
    {
      icon: "📊",
      title: "Reporting sur-mesure",
      description: "Templates personnalisés, KPIs métier, consolidation multi-entités, dashboards temps réel.",
    },
    {
      icon: "🎓",
      title: "Formation & Onboarding",
      description: "Formation équipe complète, documentation privée, hotline dédiée, support 24/7.",
    },
    {
      icon: "⚡",
      title: "SLA garanti",
      description: "99.9% uptime, temps de réponse < 2h, résolution prioritaire, compensation contractuelle.",
    },
  ];

  const industries = [
    {
      type: "Family Office",
      size: "1-5 familles patrimoniales",
      assets: "€50M - €500M+ en art",
      needs: "Consolidation patrimoniale, succession, optimisation fiscale trans-générationnelle",
    },
    {
      type: "Multi-Family Office",
      size: "5-20 familles clientes",
      assets: "Collections variées",
      needs: "Gestion déléguée, reporting clients, conformité, services à valeur ajoutée",
    },
    {
      type: "Banque Privée",
      size: "Service patrimoine artistique",
      assets: "Portefeuille clients UHNW",
      needs: "Module art intégré à l'offre de gestion de fortune, co-marquage banque",
    },
    {
      type: "Maison de ventes",
      size: "Département Private Sales",
      assets: "N/A",
      needs: "CRM collectors, historique achats/ventes, analytics performance artistes",
    },
  ];

  const securityPoints = [
    "Hébergement France/Suisse sur datacenters Tier III+ certifiés",
    "Chiffrement bout-en-bout (AES-256) des données sensibles",
    "Authentification multi-facteur obligatoire (TOTP, U2F)",
    "Audit logs immuables avec horodatage certifié",
    "Conformité RGPD, ISO 27001, SOC 2 Type II",
    "Backup quotidiens chiffrés avec rétention 7 ans",
    "Tests de pénétration annuels par cabinet tiers",
    "DPO dédié et délégué à la protection des données",
  ];

  return (
    <div className="max-w-3xl mx-auto mb-12 p-5 rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200">
      {/* Hero Section - très sobre et institutionnel */}
      <div className="bg-neutral-900 text-black">
        <div className="max-w-6xl mx-auto px-6 py-32">
          <div className="max-w-4xl">
            <div className="inline-block px-5 py-2 bg-neutral-800 text-neutral-300 text-xs font-bold tracking-widest rounded mb-8">
              ENTERPRISE SOLUTION
            </div>
            <h1 
              className="text-6xl md:text-7xl font-serif mb-8 tracking-tight leading-tight"
              style={{ fontFamily: "Georgia, serif" }}
            >
              Artkiv Family Office
            </h1>
            <p className="text-2xl text-neutral-300 mb-12 leading-relaxed max-w-3xl">
              La plateforme de gestion d'art patrimonial pour family offices,
              banques privées et institutions exigeantes.
            </p>
            <div className="flex flex-wrap gap-6 mb-12">
              <div>
                <p className="text-sm text-neutral-400 mb-1">À partir de</p>
              <p className="text-4xl font-bold">
                {t("demo.fo.price", { defaultValue: "299 €" })}
                <span className="text-2xl text-neutral-400">
                  {t("pricing.period.month")}
                </span>
              </p>
              </div>
              <div className="border-l border-neutral-700 pl-6">
                <p className="text-sm text-neutral-400 mb-1">Facturation</p>
                <p className="text-xl">Mensuelle ou annuelle</p>
              </div>
              <div className="border-l border-neutral-700 pl-6">
                <p className="text-sm text-neutral-400 mb-1">Engagement</p>
                <p className="text-xl">12 mois minimum</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => navigate("../contact?plan=family_office&enterprise=true")}
                className="px-10 py-5 bg-white text-black rounded font-semibold hover:bg-neutral-100 transition-all duration-300"
              >
                {t("demo.fo.hero.cta.quote")}
              </button>
              <button
                onClick={() => navigate("../contact?plan=family_office&demo=true")}
                className="px-10 py-5 border-2 border-white text-white rounded font-semibold hover:bg-white hover:text-black transition-all duration-300"
              >
                {t("demo.fo.hero.cta.demo")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="border-y border-neutral-200 bg-neutral-50 py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-12 text-center">
            <div>
              <p className="text-3xl font-bold mb-1">99.9%</p>
              <p className="text-sm text-neutral-600">Uptime garanti</p>
            </div>
            <div className="w-px h-12 bg-neutral-300"></div>
            <div>
              <p className="text-3xl font-bold mb-1">ISO 27001</p>
              <p className="text-sm text-neutral-600">Certifié sécurité</p>
            </div>
            <div className="w-px h-12 bg-neutral-300"></div>
            <div>
              <p className="text-3xl font-bold mb-1">RGPD</p>
              <p className="text-sm text-neutral-600">100% conforme</p>
            </div>
            <div className="w-px h-12 bg-neutral-300"></div>
            <div>
              <p className="text-3xl font-bold mb-1">&lt; 2h</p>
              <p className="text-sm text-neutral-600">Temps de réponse</p>
            </div>
            <div className="w-px h-12 bg-neutral-300"></div>
            <div>
              <p className="text-3xl font-bold mb-1">🇪🇺</p>
              <p className="text-sm text-neutral-600">Données en Europe</p>
            </div>
          </div>
        </div>
      </div>

      {/* Qui sommes-nous pour les FO */}
      <div className="max-w-6xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <h2 
              className="text-4xl font-serif mb-6"
              style={{ fontFamily: "Georgia, serif" }}
            >
              Pourquoi Artkiv pour votre Family Office ?
            </h2>
            <div className="space-y-6 text-neutral-600 leading-relaxed">
              <p>
                Les family offices et institutions financières gèrent des patrimoines
                artistiques complexes : collections multi-entités, juridictions variées,
                exigences de reporting sophistiquées.
              </p>
              <p>
                <strong className="text-black">Artkiv Family Office</strong> est la seule
                plateforme européenne pensée nativement pour ces besoins institutionnels :
                conformité réglementaire, intégrations ERP, sécurité renforcée, support dédié.
              </p>
              <p>
                Nous accompagnons nos clients Family Office depuis le kick-off jusqu'à
                l'exploitation quotidienne, avec un engagement contractuel sur la qualité
                de service.
              </p>
            </div>
          </div>
          <div className="bg-neutral-100 p-10 rounded-2xl">
            <h3 className="text-xl font-semibold mb-6">Inclus dans chaque contrat</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="text-2xl">✓</span>
                <div>
                  <p className="font-semibold">Kick-off avec votre équipe</p>
                  <p className="text-sm text-neutral-600">1 journée sur site de formation et paramétrage</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-2xl">✓</span>
                <div>
                  <p className="font-semibold">Account Manager dédié</p>
                  <p className="text-sm text-neutral-600">Point de contact unique, réunions trimestrielles</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-2xl">✓</span>
                <div>
                  <p className="font-semibold">Intégrations sur-mesure</p>
                  <p className="text-sm text-neutral-600">Connecteurs personnalisés vers votre SI existant</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-2xl">✓</span>
                <div>
                  <p className="font-semibold">SLA contractuel</p>
                  <p className="text-sm text-neutral-600">Engagements formels avec pénalités</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Features Enterprise */}
      <div className="bg-neutral-50 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-serif mb-4"
              style={{ fontFamily: "Georgia, serif" }}
            >
              Fonctionnalités Enterprise
            </h2>
            <p className="text-xl text-neutral-500">
              Tout le nécessaire pour gérer des patrimoines artistiques institutionnels
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {enterpriseFeatures.map((feature, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl border border-neutral-200 hover:border-black transition-colors">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Industries */}
      <div className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 
            className="text-4xl font-serif mb-4"
            style={{ fontFamily: "Georgia, serif" }}
          >
            Nos clients institutionnels
          </h2>
          <p className="text-xl text-neutral-500">
            Des profils variés avec un besoin commun : l'excellence
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {industries.map((industry, idx) => (
            <div key={idx} className="p-8 bg-neutral-50 rounded-2xl border-2 border-neutral-200">
              <h3 className="text-2xl font-bold mb-6">{industry.type}</h3>
              <div className="space-y-4 text-sm">
                <div className="flex gap-3">
                  <span className="text-neutral-400 w-24 flex-shrink-0">Taille</span>
                  <span className="text-neutral-700">{industry.size}</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-neutral-400 w-24 flex-shrink-0">Actifs art</span>
                  <span className="text-neutral-700">{industry.assets}</span>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="text-neutral-400 w-24 flex-shrink-0">Besoins</span>
                  <span className="text-neutral-700">{industry.needs}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sécurité & Conformité */}
      <div className="bg-black text-white py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 
                className="text-4xl font-serif mb-6"
                style={{ fontFamily: "Georgia, serif" }}
              >
                Sécurité & Conformité de niveau bancaire
              </h2>
              <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
                Vos données patrimoniales méritent le plus haut niveau de protection.
                Artkiv respecte les standards les plus stricts de l'industrie financière.
              </p>
              <button
                onClick={() => navigate("/security-compliance")}
                className="px-8 py-3 bg-white text-black rounded font-semibold hover:bg-neutral-200 transition-colors"
              >
                Voir notre politique de sécurité
              </button>
            </div>
            <div className="bg-neutral-900 p-8 rounded-2xl">
              <h3 className="text-lg font-semibold mb-6 text-neutral-300">
                Certifications & Conformité
              </h3>
              <ul className="space-y-3">
                {securityPoints.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-neutral-300 leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Process d'onboarding */}
      <div className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 
            className="text-4xl font-serif mb-4"
            style={{ fontFamily: "Georgia, serif" }}
          >
            Processus d'onboarding Enterprise
          </h2>
          <p className="text-xl text-neutral-500">
            De la signature à la production en 6-8 semaines
          </p>
        </div>

        <div className="space-y-8">
          <div className="flex gap-8 items-start">
            <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold">
              1
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Cadrage & Analyse (S1-S2)</h3>
              <p className="text-neutral-600 leading-relaxed">
                Réunions avec vos équipes (IT, Compliance, Métier). Analyse de l'existant,
                cartographie des flux de données, définition du périmètre fonctionnel.
              </p>
            </div>
          </div>

          <div className="flex gap-8 items-start">
            <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold">
              2
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Configuration & Intégrations (S3-S4)</h3>
              <p className="text-neutral-600 leading-relaxed">
                Paramétrage instance, développement connecteurs ERP/PMS, configuration SSO,
                création templates reporting, tests initiaux.
              </p>
            </div>
          </div>

          <div className="flex gap-8 items-start">
            <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold">
              3
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Migration & Formation (S5-S6)</h3>
              <p className="text-neutral-600 leading-relaxed">
                Import données historiques, formation utilisateurs (2 jours sur site),
                recette fonctionnelle, tests de charge.
              </p>
            </div>
          </div>

          <div className="flex gap-8 items-start">
            <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold">
              4
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Production & Support (S7-S8)</h3>
              <p className="text-neutral-600 leading-relaxed">
                Mise en production accompagnée, hypercare 2 semaines (disponibilité renforcée),
                bilan post-déploiement, optimisations.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Options */}
      <div className="bg-neutral-50 py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-serif mb-4"
              style={{ fontFamily: "Georgia, serif" }}
            >
              Formules tarifaires
            </h2>
            <p className="text-xl text-neutral-500">
              Choisissez la formule adaptée à votre volume
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-8 rounded-2xl border-2 border-neutral-200">
              <h3 className="text-xl font-bold mb-2">Starter</h3>
              <p className="text-4xl font-bold mb-4">299€<span className="text-lg text-neutral-500">/mois</span></p>
              <p className="text-sm text-neutral-600 mb-6">Jusqu'à 10 portefeuilles</p>
              <ul className="space-y-2 text-sm text-neutral-600">
                <li>✓ Toutes fonctionnalités</li>
                <li>✓ Support standard</li>
                <li>✓ API incluse</li>
              </ul>
            </div>

            <div className="bg-black text-white p-8 rounded-2xl border-2 border-black scale-105 shadow-xl">
              <div className="text-xs bg-white text-black px-3 py-1 rounded-full w-fit mb-4">POPULAIRE</div>
              <h3 className="text-xl font-bold mb-2">Growth</h3>
              <p className="text-4xl font-bold mb-4">599€<span className="text-lg text-neutral-400">/mois</span></p>
              <p className="text-sm text-neutral-300 mb-6">Jusqu'à 50 portefeuilles</p>
              <ul className="space-y-2 text-sm">
                <li>✓ Tout Starter +</li>
                <li>✓ Account Manager</li>
                <li>✓ SLA garanti</li>
                <li>✓ Formations incluses</li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-2xl border-2 border-neutral-200">
              <h3 className="text-xl font-bold mb-2">Enterprise</h3>
              <p className="text-4xl font-bold mb-4">Sur devis</p>
              <p className="text-sm text-neutral-600 mb-6">Portefeuilles illimités</p>
              <ul className="space-y-2 text-sm text-neutral-600">
                <li>✓ Tout Growth +</li>
                <li>✓ Infrastructure dédiée</li>
                <li>✓ Développements sur-mesure</li>
                <li>✓ SLA premium</li>
              </ul>
            </div>
          </div>

          <p className="text-center text-sm text-neutral-500 mt-8">
            * Tarifs HT, engagement 12 mois minimum. -15% si facturation annuelle.
          </p>
        </div>
      </div>

      {/* CTA Final */}
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <h2 
          className="text-5xl font-serif mb-6"
          style={{ fontFamily: "Georgia, serif" }}
        >
          Discutons de vos besoins
        </h2>
        <p className="text-xl text-neutral-500 mb-12 max-w-2xl mx-auto leading-relaxed">
          Chaque Family Office est unique. Contactez-nous pour une présentation
          personnalisée et un devis sur-mesure.
        </p>
        <div className="flex flex-wrap gap-4 justify-center mb-8">
          <button
            onClick={() => navigate("/contact?plan=family_office&enterprise=true")}
            className="px-12 py-5 bg-black text-white rounded font-bold hover:bg-neutral-800 transition-all duration-300 text-lg"
          >
            Demander un devis
          </button>
          <button
            onClick={() => navigate("/contact?plan=family_office&demo=true")}
            className="px-12 py-5 border-2 border-neutral-300 text-neutral-700 rounded font-semibold hover:border-black hover:text-black transition-all duration-300 text-lg"
          >
            Réserver une démo
          </button>
        </div>
        <div className="flex flex-wrap justify-center gap-8 text-sm text-neutral-500">
          <span>✓ Devis sous 48h</span>
          <span>✓ Démo personnalisée</span>
          <span>✓ POC possible</span>
          <span>✓ NDA fourni</span>
        </div>
      </div>
    </div>
  );
}